/**
 * 管理端多国支持：登录用户国家上下文工具。
 * 契约来源：《渠道中心接口文档-管理端多国支持_V1.2》3.2 / 模块 A。
 * - countryCode: 'NG' / 'GH'（用户未设置时后端按 NG 返回）
 * - bizCode: 'pocketbuy'（NG）/ 'pocketbuy_gh'（GH），所有 /api/admin/** 请求头必传
 * 国家->bizCode 映射在本文件单一维护，store 与请求拦截器共用。
 */

export const DEFAULT_COUNTRY_CODE = 'NG'
export const DEFAULT_BIZ_CODE = 'pocketbuy'

const BIZ_CODE_BY_COUNTRY = {
  NG: 'pocketbuy',
  GH: 'pocketbuy_gh'
}

/** 归一化国家码：仅认 NG/GH；'unset'/空/非法值按 NG 处理（登录用户契约：未设置默认 NG） */
export function normalizeCountryCode(raw) {
  const v = String(raw == null ? '' : raw).trim().toUpperCase()
  return v === 'GH' ? 'GH' : 'NG'
}

/** 国家码 -> bizCode（NG=pocketbuy / GH=pocketbuy_gh） */
export function bizCodeOfCountry(countryCode) {
  const cc = normalizeCountryCode(countryCode)
  return BIZ_CODE_BY_COUNTRY[cc] || DEFAULT_BIZ_CODE
}

/**
 * 读取登录用户关联国家（Vuex 优先，localStorage 兜底）。
 * 无登录态（无 token，如登录请求本身）返回 ''，调用方据此决定不注入 bizCode 头。
 * 已登录但历史数据无 countryCode（旧版本写入）时兼容回落 NG。
 */
export function getUserCountry() {
  let hasToken = false
  try {
    // 惰性 require 避免与 store 模块形成静态循环依赖
    const storeModule = require('@/store')
    const storeInstance = storeModule.default || storeModule
    hasToken = !!(storeInstance.getters && storeInstance.getters.token)
  } catch (e) {
    hasToken = false
  }
  if (!hasToken) {
    hasToken = !!localStorage.getItem('admin_token')
  }
  if (!hasToken) return ''

  let countryCode = ''
  try {
    const storeModule = require('@/store')
    const storeInstance = storeModule.default || storeModule
    countryCode = (storeInstance.getters && storeInstance.getters.countryCode) || ''
    if (!countryCode && storeInstance.state) {
      countryCode = storeInstance.state.countryCode || ''
    }
  } catch (e) {
    countryCode = ''
  }
  if (!countryCode) {
    try {
      const info = JSON.parse(localStorage.getItem('admin_user_info') || '{}')
      countryCode = info.countryCode || info.country_code || ''
    } catch (e) {
      countryCode = ''
    }
  }
  if (!countryCode) {
    countryCode = localStorage.getItem('admin_country_code') || ''
  }
  return normalizeCountryCode(countryCode)
}

/** 读取登录用户 bizCode；无登录上下文（未登录）返回 ''，拦截器据此不注入该请求头 */
export function getUserBizCode() {
  const countryCode = getUserCountry()
  if (!countryCode) return ''
  return bizCodeOfCountry(countryCode)
}

/** 当前登录用户是否关联加纳（GH） */
export function isGH() {
  return getUserCountry() === 'GH'
}

/**
 * 国家本位币（NG=NGN ₦ / GH=GHS GH₵）。
 * 契约：《多国支持改造方案 V1.3》国家差异表——金额字段语义 = 行记录国家的本位币。
 */
const CURRENCY_BY_COUNTRY = {
  NG: { code: 'NGN', symbol: '₦', locale: 'en-NG' },
  GH: { code: 'GHS', symbol: 'GH₵', locale: 'en-GH' }
}

/** 国家码 -> 本位币信息；未指定时取当前登录用户国家 */
export function currencyOfCountry(countryCode) {
  const cc = normalizeCountryCode(countryCode || getUserCountry() || DEFAULT_COUNTRY_CODE)
  return CURRENCY_BY_COUNTRY[cc] || CURRENCY_BY_COUNTRY.NG
}

/**
 * 金额格式化（登录用户国家本位币）：1599 -> '₦1,599' / 'GH₵1,599'。
 * @param value 数值或数字字符串；空值返回 '-'
 * @param minFraction 最小小数位（默认 0；月供等金额传 2）
 */
export function formatCurrency(value, minFraction) {
  if (value === null || value === undefined || value === '') return '-'
  const c = currencyOfCountry()
  const opts = minFraction ? { minimumFractionDigits: minFraction, maximumFractionDigits: minFraction } : undefined
  return c.symbol + Number(value).toLocaleString(c.locale, opts)
}

/**
 * 加纳 16 个 Region（一级行政区），code 落库 store.state，name 用于展示。
 * 契约：《管理端多国支持_V1.2》AD-STR-R3（state 字典按国家：GH=16 Region）。
 */
export const GH_REGIONS = [
  { code: 'greater_accra', name: 'Greater Accra' },
  { code: 'ashanti', name: 'Ashanti' },
  { code: 'central', name: 'Central' },
  { code: 'western', name: 'Western' },
  { code: 'eastern', name: 'Eastern' },
  { code: 'northern', name: 'Northern' },
  { code: 'upper_east', name: 'Upper East' },
  { code: 'upper_west', name: 'Upper West' },
  { code: 'volta', name: 'Volta' },
  { code: 'bono', name: 'Bono' },
  { code: 'bono_east', name: 'Bono East' },
  { code: 'ahafo', name: 'Ahafo' },
  { code: 'oti', name: 'Oti' },
  { code: 'north_east', name: 'North East' },
  { code: 'savannah', name: 'Savannah' },
  { code: 'western_north', name: 'Western North' }
]

/**
 * GH Region code -> 展示名；非 GH Region 值原样返回（兼容脏数据）。
 * 双值域兼容（CH-057 改造过渡期）：本地旧值 greater_accra 与 BNS CH-057 新值
 * greater_accra_region 均可映射（归一化去 _region 后缀后匹配）。
 */
export function ghRegionLabel(code) {
  if (!code) return ''
  const normalized = String(code).trim().toLowerCase().replace(/_region$/, '')
  const hit = GH_REGIONS.find(r => r.code === normalized)
  return hit ? hit.name : code
}
